// #1730 (W-9bc79724 as ruled 9 Oct 2026): the invitation links the founder's own Approve made on this device, held in
// this window's memory only until they close each one. Never written to storage, a URL, the conversation, a log or a
// trace, and never sent anywhere: the World keeps only the link's hash, so once closed it cannot be shown again (the
// founder withdraws that invitation and makes a new one). InvitationLinkOnceHost shows them, one at a time.
//
// Review round 1 (P2): each link is bound to the sign-in that made it (the Desk's identity and grant generation,
// worldStore). A link is dropped when that sign-in no longer owns this window (sign-out, another account, a grant
// change), when the host that shows links unmounts, and on arrival when the sign-in that asked for it is no longer the
// current one. A second link arriving while one shows waits behind it.
import type { InvitationLinkShownOnce } from "./gateInstancesClient";
import { useNeucleosWorldStore } from "./worldStore";

interface HeldLink {
  readonly link: InvitationLinkShownOnce;
  readonly owner: string;
}

let queue: ReadonlyArray<HeldLink> = [];
let hosts = 0;
const listeners = new Set<() => void>();
const changed = () => {
  for (const listener of listeners) listener();
};
const replace = (next: ReadonlyArray<HeldLink>) => {
  if (next.length === queue.length && next.every((held, index) => held === queue[index])) return;
  queue = next;
  changed();
};

/** The sign-in that owns this window now, or null when none does. Taken when an Approve is pressed. */
export function invitationLinkOwner(): string | null {
  const state = useNeucleosWorldStore.getState();
  return state.signedIn && state.identity !== null
    ? JSON.stringify([state.identity, state.grantRevision])
    : null;
}

/**
 * Holds a link for the host to show, if `owner` (the sign-in that pressed Approve) still owns this window and a host is
 * mounted to show it. Answers whether it is held; a link that is not is gone (the World cannot show it again).
 */
export function showInvitationLinkOnce(
  link: InvitationLinkShownOnce,
  owner: string | null,
): boolean {
  if (owner === null || hosts === 0 || owner !== invitationLinkOwner()) return false;
  replace([...queue, { link, owner }]);
  return true;
}

/** The link shown now (the first waiting), or null. */
export const readShownInvitationLink = (): InvitationLinkShownOnce | null => queue[0]?.link ?? null;

/** Done on the link shown now: it is forgotten, and the next one waiting, if any, is shown. */
export function forgetShownInvitationLink(): void {
  replace(queue.slice(1));
}

export function subscribeShownInvitationLink(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

// A sign-out, another account or a grant change drops every link the earlier sign-in made. Watched only while a host
// is mounted (nothing is held otherwise), so importing this module subscribes to nothing.
let unwatch: (() => void) | null = null;
const dropOtherSignIns = () => {
  const owner = invitationLinkOwner();
  if (queue.some((held) => held.owner !== owner))
    replace(queue.filter((held) => held.owner === owner));
};

/** The host that shows links registers while mounted; when the last one unmounts, every held link is forgotten. */
export function registerInvitationLinkHost(): () => void {
  hosts += 1;
  unwatch ??= useNeucleosWorldStore.subscribe(dropOtherSignIns);
  return () => {
    hosts -= 1;
    if (hosts > 0) return;
    unwatch?.();
    unwatch = null;
    replace([]);
  };
}
